/**
 * Load a project into the editor stores before a screen paints.
 * Movie Studio and the design editor share this so a reopen is not blank.
 */

import type { Project } from '@canvas/shared-types';
import { resolveProjectForEditor } from '@/lib/projectPages';
import { migrateVideoProject } from '@/lib/video/timelineEdit';
import { useCanvasStore } from '@/stores/canvasStore';
import { useProjectStore } from '@/stores/projectStore';
import { useVideoStore } from '@/stores/videoStore';

/** Write the open project into the stores the screens read. */
export function syncProjectIntoStores(project: Project): void {
  const resolved = resolveProjectForEditor(project);

  useProjectStore.getState().setCurrentProject({
    ...project,
    pages: resolved.pages,
    activePageId: resolved.activePageId,
    canvasData: resolved.canvasData,
  });

  const canvasStore = useCanvasStore.getState();
  canvasStore.setCanvasData(resolved.canvasData);
  canvasStore.clearHistory();
  canvasStore.markDirty(false);
}

/** A movie always has a timeline, including older files that only had pages. */
export function ensureMovieTimeline(): void {
  const video = useVideoStore.getState();
  if (!video.video?.enabled) video.enableMotion();
  const project = useProjectStore.getState().currentProject;
  const timeline = useVideoStore.getState().video;
  if (project && timeline) {
    const migrated = migrateVideoProject(timeline, { width: project.width, height: project.height });
    if (migrated !== timeline) {
      useVideoStore.getState().changeTimeline(() => migrated, { record: false });
    }
  }
  const current = useProjectStore.getState().currentProject;
  if (current && current.kind !== 'movie') {
    useProjectStore.getState().updateProject({ ...current, kind: 'movie' });
    useCanvasStore.getState().markDirty(true);
  }
}
